import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import {
  AppShell,
  Box,
  Button,
  ButtonProps,
  Drawer,
  DrawerProps,
  Group,
  GroupProps,
  Menu,
  Stack,
  Text,
  UnstyledButton,
  createPolymorphicComponent,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowRight,
  IconSettings,
  IconMenu2,
  IconDoorEnter,
  IconLogout,
  IconUser,
} from "@tabler/icons-react";
import Logo from "app/ui/assets/logo/logo-wide-white.svg";
import {
  PrimaryTopNav,
  primaryTopNavLogoHeight,
  primaryTopNavParentGroupHeight,
  FifteenFiveImage,
} from "app/ui/components/primary-top-nav";
import { querySelectorAllAsync } from "app/ui/utils/querySelectorAllAsync";
import _ from "lodash";
import Image from "next/image";
import { useRouter } from "next/router";

import { useReturnToFifteenFiveUrl } from "hooks/15five";
import { useAuthGateDisclosure } from "hooks/useQueryParamDisclosure";
import { UserWithCompany } from "lib/frontend/cm/utils/types";
import { getBelongsToFifteenFivePartner } from "lib/shared/partners";
import { EcsUserType } from "lib/shared/users/getEcsUserType";
import { CompanyType } from "prisma/cm/client";
import { AppIcon } from "ui/components/icons/AppIcon";
import { constrainImageDimensions } from "ui/utils/constrainImageDimensions";

import { useTrackerAuth } from "./AuthProvider";
import { HenCalendlySchedulerButton } from "./HenCalendlySchedulerButton";
import { PaidFeatureModal, usePaidFeatureModalDisclosure } from "./PaidFeatureModal";

const TRACKER_MAIN_NAV_ID = "tracker-header-main-nav";

function TrackerMainNavCtaGroup(groupProps: GroupProps) {
  const { user, pending } = useTrackerAuth();
  const [, { open }] = useAuthGateDisclosure();

  const router = useRouter();
  const isPublicBenchmarkingSite = router.pathname.startsWith("/benchmarking/s");

  // For public benchmarking site, show login buttons even while auth is pending
  // This prevents showing an empty gray header
  if (pending && !isPublicBenchmarkingSite) return null;

  return (
    <Group {...groupProps}>
      {user ? (
        <HeaderAvatarMenuButton />
      ) : (
        <>
          {isPublicBenchmarkingSite ? (
            <Button color="bay" sx={{ boxShadow: "none" }} onClick={open}>
              Login
            </Button>
          ) : (
            <Button
              variant="filled"
              color="bay"
              sx={{ boxShadow: "none" }}
              component="a"
              href="/login"
            >
              Login
            </Button>
          )}
          <HenCalendlySchedulerButton
            variant="filled"
            color="bay"
            bg="bay.6"
            sx={{ boxShadow: "none" }}
            rightSection={<IconArrowRight size={16} stroke={2.5} />}
          >
            Schedule Demo
          </HenCalendlySchedulerButton>
        </>
      )}
    </Group>
  );
}

// @ts-expect-error TS7006
function getIsPayingCustomer(user) {
  return (
    Boolean(user?.company) &&
    ![CompanyType.VC, CompanyType.ECS].includes(user?.company?.companyType)
  );
}

export function useShowEmployeeDashboardUi(user: UserWithCompany | null) {
  if (!user?.company) return false;
  return user && user.company.enableEmployeeDashboard && user.company.enableEmployeeDashboardAccess;
}

export function TrackerHeaderMainNav({ handleOpenDrawer }: { handleOpenDrawer: () => void }) {
  const { user, pending } = useTrackerAuth();
  const router = useRouter();

  const isFifteenFivePartner = getBelongsToFifteenFivePartner(user);

  const isVcCompany = user?.company?.companyType === CompanyType.VC;
  const isNonCmEcsParticipant = user?.ecsUserType === EcsUserType.NON_CM_ECS_PARTICIPANT;
  const isUnpublishedEcsParticipant = user?.createdViaEcs && !isNonCmEcsParticipant;

  const isPayingCustomer = getIsPayingCustomer(user) && !isUnpublishedEcsParticipant;

  const userIsNonAdmin = user?.role !== "COMPANY_ADMIN";

  const isBenchmarking = router.pathname.startsWith("/benchmarking/");

  const [paidFeatureModalState, paidFeatureModalHandlers] = usePaidFeatureModalDisclosure();
  const showEmployeeDashboardUi = useShowEmployeeDashboardUi(user);

  const isPublicBenchmarkingSite = router.pathname.startsWith("/benchmarking/s");
  // Don't show gray background for public benchmarking site even when pending
  // This prevents showing an empty gray header
  const bgColor = !pending
    ? isFifteenFivePartner
      ? "fifteenFive.1"
      : undefined
    : isPublicBenchmarkingSite
      ? undefined
      : "gray.6";

  return (
    <>
      <Box id={TRACKER_MAIN_NAV_ID}>
        <PrimaryTopNav.ParentGrid w="100%" {...(bgColor ? { bg: bgColor } : {})}>
          <PrimaryTopNav.SideSection>
            <Stack justify="center" h="100%">
              {!isFifteenFivePartner ? (
                <UnstyledButton component="a" href={"/"} display="inline-block">
                  <Image
                    priority
                    src={Logo}
                    {...constrainImageDimensions({ ...Logo, newHeight: primaryTopNavLogoHeight })}
                    alt="Comprehensive"
                  />
                </UnstyledButton>
              ) : (
                <a href={"/"}>
                  <Group wrap="nowrap" align="center" h="100%">
                    <FifteenFiveImage />
                    <Image
                      priority
                      src={Logo}
                      height={22}
                      width={90}
                      style={{ marginTop: 4 }}
                      alt="Comprehensive"
                    />
                  </Group>
                </a>
              )}
            </Stack>
          </PrimaryTopNav.SideSection>
          <PrimaryTopNav.MainSection>
            {pending ? null : (
              <PrimaryTopNav.LinkGroup
                justify="center"
                display={{ base: "none", lg: "flex" }}
                pos="relative"
                left={isFifteenFivePartner ? 100 : 0}
                sx={{ marginLeft: isBenchmarking ? 150 : 0 }}
              >
                {!isVcCompany ? (
                  <PrimaryTopNav.LinkAtom
                    isBenchmarking
                    {...(isPayingCustomer
                      ? {
                          component: "a",
                          href: "/reviews",
                          target: "_top",
                        }
                      : {
                          onClick: () => paidFeatureModalHandlers.open("reviews"),
                        })}
                    leftSection={<AppIcon.Cycles size={16} color="white" />}
                  >
                    {isPayingCustomer ? "Cycles" : "Comp Cycles"}
                  </PrimaryTopNav.LinkAtom>
                ) : null}

                {isPayingCustomer ? (
                  <PrimaryTopNav.LinkAtom
                    isBenchmarking
                    // @ts-expect-error TS2322
                    component="a"
                    href="/company/employees"
                    leftSection={<AppIcon.Employees size={16} color="white" />}
                    target="_top"
                  >
                    Employees
                  </PrimaryTopNav.LinkAtom>
                ) : null}
                {!isVcCompany ? (
                  <PrimaryTopNav.LinkAtom
                    isBenchmarking
                    {...(isPayingCustomer
                      ? {
                          component: "a",
                          href: "/company/pay-ranges",
                          target: "_top",
                        }
                      : {
                          onClick: () => paidFeatureModalHandlers.open("pay-ranges"),
                        })}
                    leftSection={<AppIcon.Ranges size={16} color="white" />}
                  >
                    Pay Ranges
                  </PrimaryTopNav.LinkAtom>
                ) : null}
                {isVcCompany ? (
                  <PrimaryTopNav.LinkAtom
                    isBenchmarking
                    // @ts-expect-error TS2322
                    component="a"
                    href={"/vc"}
                    leftSection={<AppIcon.VcDashboard size={16} color="white" />}
                    target="_top"
                  >
                    Firm Information
                  </PrimaryTopNav.LinkAtom>
                ) : null}
                {isPayingCustomer ? (
                  <PrimaryTopNav.LinkAtom
                    isBenchmarking
                    // @ts-expect-error TS2322
                    component="a"
                    href={userIsNonAdmin ? "/benchmarking/s" : "/benchmarking-dashboard"}
                    leftSection={<AppIcon.Benchmarking size={16} color="white" />}
                    target="_top"
                    isActive
                  >
                    Benchmarking
                  </PrimaryTopNav.LinkAtom>
                ) : (
                  <PrimaryTopNav.LinkAtom
                    isBenchmarking
                    // @ts-expect-error TS2322
                    component="a"
                    href={
                      isVcCompany || isNonCmEcsParticipant
                        ? "/benchmarking/ecs?ecsDataset=exec"
                        : "/benchmarking/s"
                    }
                    leftSection={<AppIcon.Benchmarking size={16} color="white" />}
                    target="_top"
                    isActive
                  >
                    Benchmarking
                  </PrimaryTopNav.LinkAtom>
                )}
                {isPayingCustomer ? (
                  showEmployeeDashboardUi ? (
                    <PrimaryTopNav.LinkAtom
                      isBenchmarking
                      // @ts-expect-error TS2322
                      component="a"
                      href="/personal/total-rewards"
                      target="_top"
                      leftSection={<AppIcon.TotalRewards size={16} color="white" />}
                    >
                      Total Rewards
                    </PrimaryTopNav.LinkAtom>
                  ) : null
                ) : !isVcCompany ? (
                  <PrimaryTopNav.LinkAtom
                    isBenchmarking
                    // @ts-expect-error TS2322
                    onClick={() => paidFeatureModalHandlers.open("total-rewards")}
                    leftSection={<AppIcon.TotalRewards size={16} color="white" />}
                  >
                    Total Rewards
                  </PrimaryTopNav.LinkAtom>
                ) : null}
              </PrimaryTopNav.LinkGroup>
            )}
          </PrimaryTopNav.MainSection>
          <PrimaryTopNav.SideSection span={1}>
            <TrackerMainNavCtaGroup
              gap={12}
              justify="flex-end"
              h="100%"
              display={{ base: "none", lg: "flex" }}
            />
            <Stack h="100%" justify="center" align="end" display={{ base: "flex", lg: "none" }}>
              <Box>
                <Button
                  variant="transparent"
                  sx={{ boxShadow: "none" }}
                  size="compact-md"
                  px={5}
                  mr={-5}
                  onClick={handleOpenDrawer}
                >
                  <IconMenu2 color="white" size={16} />
                </Button>
              </Box>
            </Stack>
          </PrimaryTopNav.SideSection>
        </PrimaryTopNav.ParentGrid>
      </Box>
      {isPayingCustomer ? null : (
        <PaidFeatureModal {...paidFeatureModalState} onClose={paidFeatureModalHandlers.close} />
      )}
    </>
  );
}

function removeParens(str: string) {
  return str.replace(/\([^()]*\)/g, "");
}

export function getInitials(name: string) {
  const tokens = removeParens(name).replace(/\s+/g, " ").trim().split(" ");
  const firstToken = _.first(tokens) || "";
  const lastToken = (tokens.length > 1 && _.last(tokens)) || "";
  const firstInit = firstToken[0];
  const lastInit = lastToken[0];
  return [firstInit, lastInit].join("").toUpperCase();
}

function HeaderAvatarMenuButton() {
  const { user } = useTrackerAuth();
  if (!user) throw Error("Need a user.");

  const initials = useMemo(() => getInitials(user?.name || ""), [user?.name]);
  const fifteenFiveUrl = useReturnToFifteenFiveUrl();

  const logoutRedirectUrl = null;
  // const logoutRedirectUrl = useMemo(() => {
  //   if (typeof window === "undefined") return null;
  //   const { pathname } = window.location;
  //   if (pathname.startsWith("/benchmarking")) return pathname;
  //   return null;
  // }, []);

  return (
    <Menu position="bottom-end">
      <Menu.Target>
        <PrimaryTopNav.Avatar>{initials}</PrimaryTopNav.Avatar>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item sx={{ backgroundColor: "transparent !important", cursor: "auto" }}>
          <Text>{user?.name}</Text>
          <Text fz="xs" sx={{ fontWeight: "normal" }} c="gray.6">
            {user?.company?.name}
          </Text>
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item leftSection={<IconUser size={16} />} component="a" href="/account">
          My account
        </Menu.Item>
        {fifteenFiveUrl ? (
          <Menu.Item leftSection={<IconDoorEnter size={16} />} component="a" href={fifteenFiveUrl}>
            Return to 15Five
          </Menu.Item>
        ) : null}
        {user?.role === "COMPANY_ADMIN" ? (
          <Menu.Item
            leftSection={<IconSettings size={16} />}
            component="a"
            href="/settings/application/company"
          >
            Company settings
          </Menu.Item>
        ) : null}
        <Menu.Item
          leftSection={<IconLogout size={16} />}
          component="a"
          href={`/logout${logoutRedirectUrl ? `?redirectUrl=${logoutRedirectUrl}` : ""}`}
        >
          Log out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

const MobileDrawerButton = createPolymorphicComponent<"button", ButtonProps>(
  // eslint-disable-next-line react/display-name
  forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    return <Button variant={"subtle"} size={"lg"} {...props} ref={ref} />;
  }),
);

function useDrawerOffsetTop(opened: boolean, selector: string) {
  const [offset, setOffset] = useState<number | null>(null);

  const handleSyncOffsetPosition = useCallback(async () => {
    const nodeList = await querySelectorAllAsync(selector);
    if (nodeList.length !== 1) {
      console.error("Couldn't determine offset.");
      setOffset(primaryTopNavParentGroupHeight); // a guess - better that than break the entire thing :shrug:
      return;
    }
    const navNode = nodeList[0];
    const { bottom } = navNode.getBoundingClientRect();
    setOffset(bottom);
  }, [setOffset]);

  useEffect(() => {
    if (!opened) {
      if (_.isNil(offset)) setOffset(null);
      return;
    }
    handleSyncOffsetPosition();
  }, [opened]);

  return offset;
}

function TrackerHeaderMobileDrawer(drawerProps: DrawerProps) {
  const { opened, ...drawerPropsRest } = drawerProps;
  const { user } = useTrackerAuth();
  const isPayingCustomer = getIsPayingCustomer(user);

  /*
   * We don't know how big this offset is in the event
   * that there's additional content on top of the main
   * header, so we compute dynamically.
   */
  const offset = useDrawerOffsetTop(opened, `#${TRACKER_MAIN_NAV_ID}`);

  const [paidFeatureModalState, paidFeatureModalHandlers] = usePaidFeatureModalDisclosure();
  return (
    <>
      <Drawer
        {...drawerPropsRest}
        opened={opened && !_.isNil(offset)}
        withOverlay={false}
        closeOnClickOutside
        styles={{
          content: {
            // TODO: This is hardcoded based on the height of the header. Should
            // be able to compute this.
            top: offset || 0,
            height: `calc(100vh - ${offset}px)`,
            transition: "transform 250ms cubic-bezier(0.645, 0.045, 0.355, 1) 0s",
          },
        }}
        size="sm"
        position="top"
      >
        <Stack
          sx={{
            maxWidth: "100%",
            margin: "0 auto",
          }}
        >
          <MobileDrawerButton
            {...(isPayingCustomer
              ? {
                  component: "a",
                  href: "/reviews",
                  target: "_top",
                }
              : {
                  onClick: () => paidFeatureModalHandlers.open("reviews"),
                })}
            size="lg"
            leftSection={<AppIcon.Cycles size={16} />}
          >
            Comp Cycles
          </MobileDrawerButton>
          {isPayingCustomer ? (
            <MobileDrawerButton size="lg" leftSection={<AppIcon.Employees size={16} />}>
              Employees
            </MobileDrawerButton>
          ) : null}
          <MobileDrawerButton
            {...(isPayingCustomer
              ? {
                  component: "a",
                  href: "/company/pay-ranges",
                  target: "_top",
                }
              : {
                  onClick: () => paidFeatureModalHandlers.open("pay-ranges"),
                })}
            size="lg"
            leftSection={<AppIcon.Ranges size={16} />}
          >
            Pay Ranges
          </MobileDrawerButton>
          <MobileDrawerButton
            leftSection={<AppIcon.Benchmarking size={16} />}
            onClick={drawerProps.onClose}
            size="lg"
          >
            Benchmarking
          </MobileDrawerButton>
          {isPayingCustomer ? null : (
            <MobileDrawerButton
              onClick={() => paidFeatureModalHandlers.open("total-rewards")}
              leftSection={<AppIcon.TotalRewards size={16} />}
              size="lg"
            >
              Total Rewards
            </MobileDrawerButton>
          )}
        </Stack>
      </Drawer>
      {isPayingCustomer ? null : (
        <PaidFeatureModal {...paidFeatureModalState} onClose={paidFeatureModalHandlers.close} />
      )}
    </>
  );
}

export function TrackerHeader() {
  const [navDrawerOpened, navDrawerHandlers] = useDisclosure(false);

  return (
    <AppShell.Header h={primaryTopNavParentGroupHeight} withBorder={false}>
      {/* <MarketingHomeLinkBanner /> */}
      <TrackerHeaderMainNav handleOpenDrawer={navDrawerHandlers.open} />
      <TrackerHeaderMobileDrawer opened={navDrawerOpened} onClose={navDrawerHandlers.close} />
    </AppShell.Header>
  );
}
